import { Close, DeleteOutlined } from '@mui/icons-material'
import './QuoteValidation.css'
import {
  Alert,
  Chip,
  TableCell,
  TableRow,
  TableHead,
  Table,
  Typography,
  Button,
  TextField,
  TableBody,
  IconButton,
  TableContainer,
  TablePagination,
  Autocomplete,
} from '@mui/material'
import { CurrencyInput, DatePicker } from '@impres-development/assets/client'
import { useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import axios from 'axios'
import dayjs from 'dayjs'
import { quoteLineType, supplierQuoteImportStateType } from '../types'
import { useCatalogData } from '../CatalogList/useCatalogData'
import { useManufacturerData } from '../ManufacturerList/useManufacturerData'
import { usePagination } from '../components/shared/usePagination'
import { SupplierSearchAutocomplete } from 'src/helper'

const UNIT_COST_WIDTH = '130px'
const EXT_COST_WIDTH = '140px'

function recomputeExtCost(lineItem: quoteLineType, patch: Partial<Pick<quoteLineType, 'Qty' | 'UnitCost'>>) {
  const qty = lineItem.Qty ?? 0
  const unitCost = lineItem.UnitCost ?? 0
  const currentExtCost = lineItem.ExtCost ?? 0
  const matchesFormula = Math.abs(currentExtCost - qty * unitCost) < 0.01
  return matchesFormula ? (patch.Qty ?? qty) * (patch.UnitCost ?? unitCost) : currentExtCost
}

function withSupplierPartNumber(lineItem: quoteLineType): quoteLineType {
  if (lineItem.VendorPartNumber?.trim()) return lineItem
  return { ...lineItem, VendorPartNumber: lineItem.MfrPartNumber }
}

function isBlank(value: string | null | undefined) {
  return !value?.trim()
}

function QuoteValidation() {
  const navigate = useNavigate()
  const location = useLocation()
  const { quoteId: routeQuoteId } = useParams()
  const locationState = (location.state ?? {}) as Partial<supplierQuoteImportStateType>
  const { rows: catalogRows } = useCatalogData()
  const contractCatalogOptions = useMemo(
    () => Array.from(new Set(catalogRows.map(row => row.contractName))).sort(),
    [catalogRows],
  )
  const { rows: manufacturerRows } = useManufacturerData()
  const manufacturerOptions = useMemo(() => manufacturerRows.map(row => row.supplier).sort(), [manufacturerRows])
  const [validationLoading, setValidationLoading] = useState(false)
  const [totalAmount, setTotalAmount] = useState<number | null>(locationState.totalAmount ?? null)
  const [lineItems, setLineItems] = useState<quoteLineType[]>(() =>
    (locationState.lineItems || []).map(withSupplierPartNumber),
  )
  const [importError, setImportError] = useState<string | null>(null)
  const [supplier, setSupplier] = useState<string>(locationState.supplier || '')
  const [quoteNumber, setQuoteNumber] = useState<string>(locationState.quoteNumber || '')
  const quoteId = routeQuoteId ?? locationState.quoteId
  const [expirationDate, setExpirationDate] = useState<Date | null>(locationState.expirationDate ?? null)
  const sortedLineItems = useMemo(() => [...lineItems].sort((a, b) => a.LineNumber - b.LineNumber), [lineItems])
  const {
    page,
    rowsPerPage,
    pagedItems: pagedLineItems,
    totalCount,
    handlePageChange,
    handleRowsPerPageChange,
  } = usePagination(sortedLineItems, 25)

  async function validateQuote() {
    const missingPartNumbers = sortedLineItems
      .filter(lineItem => isBlank(lineItem.VendorPartNumber) || isBlank(lineItem.MfrPartNumber))
      .map(lineItem => lineItem.LineNumber)
    if (missingPartNumbers.length > 0) {
      const shown = missingPartNumbers.slice(0, 10).join(', ')
      const more = missingPartNumbers.length > 10 ? ` and ${missingPartNumbers.length - 10} more` : ''
      setImportError(`Supplier Part # and Mfr Part # are required. Fill them in on line ${shown}${more}.`)
      return
    }

    setImportError(null)
    setValidationLoading(true)
    lineItems.forEach(lineItem => {
      lineItem.LineNumber = Number(lineItem.LineNumber)
      lineItem.Qty = Number(lineItem.Qty) ? Number(lineItem.Qty) : 0
      lineItem.UnitCost = Number(lineItem.UnitCost) ? Number(lineItem.UnitCost) : 0
      lineItem.ExtCost = Number(lineItem.ExtCost) ? Number(lineItem.ExtCost) : 0
      lineItem.ContractCeilingPrice = Number(lineItem.ContractCeilingPrice) ? Number(lineItem.ContractCeilingPrice) : 0
    })

    const saveVendorQuote = () =>
      axios
        .post('/validateVendorQuote', {
          lineItems: lineItems,
          header: {
            quoteId: quoteId,
            vendorQuoteNumber: quoteNumber,
            vendorName: supplier,
            expirationDate: expirationDate,
            totalAmount: totalAmount,
          },
        })
        .then(() => {
          navigate('/quote/' + quoteId, { state: { refreshLineItems: true } })
        })
        .catch(err => {
          console.error(err)
          setImportError('The supplier quote could not be imported. Please try again.')
        })
        .finally(() => {
          setValidationLoading(false)
        })

    if (!locationState.file) {
      saveVendorQuote()
      return
    }

    const formData = new FormData()
    formData.append('file', locationState.file)

    axios
      .post(`/quote/${quoteId}/vendorQuote`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      .then(saveVendorQuote)
      .catch(err => {
        console.log(err)
        setImportError('The supplier quote file could not be uploaded. Please try again.')
        setValidationLoading(false)
      })
  }

  return (
    <div className="quote-validation">
      <div className="quote-validation-tabs">
        <div className="quote-validation-tabs-left">
          <Typography variant="h6">Supplier Quote Confirmation</Typography>
          <Chip label={<Typography variant="h6">Quote #: {quoteNumber}</Typography>} size="medium" variant="filled" />
        </div>
        <div className="quote-validation-tabs-right">
          <TablePagination
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
            page={page}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
          />
          <Button variant="contained" onClick={validateQuote} loading={validationLoading}>
            IMPORT
          </Button>
          <IconButton
            onClick={() => {
              navigate('/quote/' + quoteId)
            }}
          >
            <Close />
          </IconButton>
        </div>
      </div>
      <div className="quote-validation-header">
        <SupplierSearchAutocomplete
          value={supplier}
          onChange={value => {
            setSupplier(value as string)
          }}
          label="Supplier Name"
          fullWidth
        />
        <TextField
          fullWidth
          size="small"
          label="Supplier Quote #"
          value={quoteNumber}
          onChange={e => {
            setQuoteNumber(e.target.value)
          }}
        />
        <DatePicker
          fullWidth
          size="small"
          label="Expiration Date"
          value={expirationDate ? dayjs(expirationDate) : null}
          onChange={value => {
            setExpirationDate(value?.toDate() ?? null)
          }}
        />
        <CurrencyInput
          value={totalAmount}
          size="small"
          label="Total Amount"
          fullWidth
          required
          onChange={value => {
            setTotalAmount(value)
          }}
        />
      </div>
      {importError && (
        <Alert severity="error" className="quote-validation-error" onClose={() => setImportError(null)}>
          {importError}
        </Alert>
      )}
      <TableContainer className="quote-validation-table-container">
        <Table stickyHeader>
          <TableHead className="quote-validation-table-header">
            <TableRow>
              <TableCell width="5%">Line #</TableCell>
              <TableCell width="9%">Supplier</TableCell>
              <TableCell width="9%">Supplier Part #</TableCell>
              <TableCell width="9%">Manufacturer</TableCell>
              <TableCell width="8%">MFR Part #</TableCell>
              <TableCell width="16%">Description</TableCell>
              <TableCell width="5%" align="right">
                Qty
              </TableCell>
              <TableCell width={UNIT_COST_WIDTH} align="right">
                Unit Cost
              </TableCell>
              <TableCell width={EXT_COST_WIDTH} align="right">
                Ext Cost
              </TableCell>
              <TableCell width="10%">Contract Catalog</TableCell>
              <TableCell width="8%" align="right">
                GSA Price
              </TableCell>
              <TableCell width="4%"></TableCell>
            </TableRow>
          </TableHead>
          <TableBody className="quote-validation-table-body">
            {pagedLineItems.map(item => (
              <TableRow key={item.LineNumber}>
                <TableCell>{item.LineNumber}</TableCell>
                <TableCell>
                  <SupplierSearchAutocomplete
                    value={item.Provider}
                    onChange={value => {
                      setLineItems(prev =>
                        prev.map(lineItem =>
                          lineItem.LineNumber === item.LineNumber
                            ? { ...lineItem, Provider: value as string }
                            : lineItem,
                        ),
                      )
                    }}
                    label="Supplier"
                    fullWidth
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    fullWidth
                    label="Supplier Part #"
                    multiline
                    required
                    error={isBlank(item.VendorPartNumber)}
                    size="small"
                    value={item.VendorPartNumber}
                    onChange={e => {
                      setLineItems(prev =>
                        prev.map(lineItem =>
                          lineItem.LineNumber === item.LineNumber
                            ? { ...lineItem, VendorPartNumber: e.target.value }
                            : lineItem,
                        ),
                      )
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Autocomplete
                    fullWidth
                    size="small"
                    value={item.Manufacturer}
                    onChange={(_, value) => {
                      setLineItems(prev =>
                        prev.map(lineItem =>
                          lineItem.LineNumber === item.LineNumber
                            ? { ...lineItem, Manufacturer: value as string }
                            : lineItem,
                        ),
                      )
                    }}
                    options={manufacturerOptions}
                    renderInput={params => <TextField {...params} label="Manufacturer" multiline />}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    fullWidth
                    label="Mfr Part #"
                    multiline
                    required
                    error={isBlank(item.MfrPartNumber)}
                    size="small"
                    value={item.MfrPartNumber}
                    onChange={e => {
                      setLineItems(prev =>
                        prev.map(lineItem =>
                          lineItem.LineNumber === item.LineNumber
                            ? { ...lineItem, MfrPartNumber: e.target.value }
                            : lineItem,
                        ),
                      )
                    }}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    fullWidth
                    multiline
                    label="Description"
                    size="small"
                    value={item.Description}
                    onChange={e => {
                      setLineItems(prev =>
                        prev.map(lineItem =>
                          lineItem.LineNumber === item.LineNumber
                            ? { ...lineItem, Description: e.target.value }
                            : lineItem,
                        ),
                      )
                    }}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    fullWidth
                    label="Qty"
                    multiline
                    size="small"
                    value={item.Qty && !isNaN(item.Qty) ? item.Qty.toLocaleString() : 0}
                    onChange={e => {
                      setLineItems(prev =>
                        prev.map(lineItem => {
                          if (lineItem.LineNumber !== item.LineNumber) return lineItem
                          const qty = Number(e.target.value.replace(/,/g, ''))
                          return { ...lineItem, Qty: qty, ExtCost: recomputeExtCost(lineItem, { Qty: qty }) }
                        }),
                      )
                    }}
                  />
                </TableCell>
                <TableCell>
                  <CurrencyInput
                    fullWidth
                    label="Unit Cost"
                    size="small"
                    value={item.UnitCost ?? 0}
                    onChange={value => {
                      setLineItems(prev =>
                        prev.map(lineItem => {
                          if (lineItem.LineNumber !== item.LineNumber) return lineItem
                          const unitCost = value ?? 0
                          return {
                            ...lineItem,
                            UnitCost: unitCost,
                            ExtCost: recomputeExtCost(lineItem, { UnitCost: unitCost }),
                          }
                        }),
                      )
                    }}
                  />
                </TableCell>
                <TableCell>
                  <CurrencyInput
                    fullWidth
                    label="Ext Cost"
                    size="small"
                    value={item.ExtCost && !isNaN(item.ExtCost) ? item.ExtCost : 0}
                    onChange={value => {
                      setLineItems(prev =>
                        prev.map(lineItem =>
                          lineItem.LineNumber === item.LineNumber ? { ...lineItem, ExtCost: value ?? 0 } : lineItem,
                        ),
                      )
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Autocomplete
                    fullWidth
                    size="small"
                    value={item.ContractCatalog}
                    onChange={(_, value) => {
                      setLineItems(prev =>
                        prev.map(lineItem =>
                          lineItem.LineNumber === item.LineNumber
                            ? { ...lineItem, ContractCatalog: value as string }
                            : lineItem,
                        ),
                      )
                    }}
                    options={contractCatalogOptions}
                    renderInput={params => <TextField {...params} label="Contract Catalog" multiline />}
                  />
                </TableCell>
                <TableCell>
                  <CurrencyInput
                    fullWidth
                    label="GSA Price"
                    size="small"
                    value={item.ContractCeilingPrice ?? 0}
                    onChange={value => {
                      setLineItems(prev =>
                        prev.map(lineItem =>
                          lineItem.LineNumber === item.LineNumber
                            ? { ...lineItem, ContractCeilingPrice: value ?? 0 }
                            : lineItem,
                        ),
                      )
                    }}
                  />
                </TableCell>
                <TableCell>
                  <IconButton
                    onClick={() => {
                      setLineItems(prev => prev.filter(lineItem => lineItem.LineNumber !== item.LineNumber))
                    }}
                  >
                    <DeleteOutlined />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  )
}

export default QuoteValidation
